import { createSignal, createResource, createEffect, onMount, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { toast } from '../lib/toast.js';
import Modal from './Modal.jsx';
import { listBastions } from '../lib/sshbastions.js';
import { listConnectorsLite } from '../lib/connectors.js';

// Preset connection tints. '' = none; red is the canonical "production" warning.
const COLOR_PALETTE = [
  { value: '', label: 'None' },
  { value: '#ef4444', label: 'Red (prod)' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#22c55e', label: 'Green' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#6b7280', label: 'Grey' },
];

// Whole number for a Go int field: the backend decodes the JSON into an int, so
// a fractional value (2.5) or NaN would fail the WHOLE save. Blank/invalid → 0,
// which the backend treats as "default" (create) / "keep" where it applies.
const int = (v) => Math.round(Number(v)) || 0;

export default function ConnectionForm(props) {
  const init = props.initial || {};
  // Duplicate mode: prefill from an existing connection but POST a new one.
  // (Password is never sent to the client, so it can't be copied — re-enter.)
  const isDuplicate = !!props.duplicate;
  const isEdit = !!props.initial && !isDuplicate;

  const [name, setName]               = createSignal(
    isDuplicate ? `${init.name ?? ''} (copy)` : (init.name ?? ''));
  const [host, setHost]               = createSignal(init.host ?? '');
  const [port, setPort]               = createSignal(init.port ?? 3306);
  const [user, setUser]               = createSignal(init.mysqlUsername ?? '');
  const [password, setPassword]       = createSignal('');
  const [defaultDB, setDefaultDB]     = createSignal(init.defaultDatabase ?? '');
  const [group, setGroup]             = createSignal(init.group ?? '');
  const [color, setColor]             = createSignal(init.color ?? '');
  const [tlsEnabled, setTLS]          = createSignal(init.tlsEnabled ?? false);
  const [readOnly, setReadOnly]       = createSignal(init.readOnly ?? false);
  const [isShared, setIsShared]       = createSignal(init.isShared ?? false);
  const [shareTags, setShareTags]     = createSignal(init.shareTags ?? []);
  // Transport: a connection routes through AT MOST ONE of an SSH bastion
  // (forward) or a connector (reverse) — 0/0 = direct. Admin-only — non-admins
  // keep whatever the connection was created with (sticky), but can't change it.
  const [sshBastionId, setSshBastionId] = createSignal(init.sshBastionId ?? 0);
  const [connectorId, setConnectorId]   = createSignal(init.connectorId ?? 0);
  const [bastions, setBastions]       = createSignal([]);
  const [connectors, setConnectors]   = createSignal([]);

  // Tag-scoped sharing is admin-only. The picker offers tags already assigned
  // to users (the "teams"); empty selection = shared with the whole team.
  const isAdmin = () => currentUser()?.role === 'admin';
  // Who may choose a transport: admins (bastion or connector) + connection-creators
  // (connector only — bastions stay admin-managed; the bastion optgroup self-hides
  // because listBastions 403s for non-admins).
  const canPickTransport = () => {
    const u = currentUser();
    return u?.role === 'admin' || ((u?.role === 'user' || u?.role === 'subadmin') && !!u?.canCreateConnections);
  };

  // Admins can attach a bastion/connector; non-admins see the current label only.
  // Fetch both transport lists in parallel; allSettled keeps one failing from
  // hiding the other (a missing connector endpoint shouldn't blank the bastions).
  onMount(async () => {
    if (!canPickTransport()) return;
    const [b, c] = await Promise.allSettled([listBastions(), listConnectorsLite()]);
    if (b.status === 'fulfilled') setBastions(b.value);
    if (c.status === 'fulfilled') setConnectors(c.value);
  });

  // The "Connect via" select encodes the mutually-exclusive choice as one value:
  // 'direct' | 'bastion:<id>' | 'connector:<id>'. Setting it updates both signals.
  const transport = () => connectorId() ? `connector:${connectorId()}`
    : sshBastionId() ? `bastion:${sshBastionId()}` : 'direct';
  const setTransport = (v) => {
    if (v.startsWith('connector:')) { setConnectorId(Number(v.slice(10))); setSshBastionId(0); }
    else if (v.startsWith('bastion:')) { setSshBastionId(Number(v.slice(8))); setConnectorId(0); }
    else { setSshBastionId(0); setConnectorId(0); }
  };
  // The bastion/connector <option>s load async (onMount), so on an edit the
  // <select>'s value can be applied before the matching option exists — the
  // browser then shows "Direct" and Solid won't re-sync once the options arrive.
  // Re-apply the value whenever it OR the loaded lists change. (PROJECT_NOTES §5.)
  let transportSel;
  createEffect(() => {
    const v = transport();
    bastions(); connectors(); // track so this re-runs after the lists resolve
    if (transportSel) transportSel.value = v;
  });
  const currentTransportLabel = () => {
    if (connectorId()) {
      const c = connectors().find((x) => x.id === connectorId());
      return c ? `Connector: ${c.name}` : `Connector #${connectorId()}`;
    }
    if (sshBastionId()) {
      const b = bastions().find((x) => x.id === sshBastionId());
      return b ? `${b.name} (${b.user}@${b.host}:${b.port})` : `Bastion #${sshBastionId()}`;
    }
    return 'Direct (no tunnel)';
  };
  const [allTags] = createResource(
    () => (isAdmin() && isShared()) ? true : null,
    async () => {
      try { return (await api.get('/api/user-tags')).tags ?? []; }
      catch { return []; }
    }
  );
  const toggleTag = (t) => setShareTags(
    shareTags().includes(t) ? shareTags().filter((x) => x !== t) : [...shareTags(), t]
  );
  const [timeout, setTimeout_]        = createSignal(init.txnIdleTimeoutSecs ?? 600);
  const [maxTabs, setMaxTabs]         = createSignal(init.maxTabsPerUser ?? 10);
  const [poolConns, setPoolConns]     = createSignal(init.maxPoolConns ?? 20);
  // Retention cap for SERVER-side backups (Backup tab + scheduled jobs):
  // 0 = keep all; N = keep only the newest N. Enforced server-side. New
  // connections default to 7 (?? only falls back on undefined, so an existing
  // connection's explicit 0 is preserved on edit).
  const [backupRotation, setBackupRotation] = createSignal(init.backupRotation ?? 7);

  const [saving, setSaving] = createSignal(false);
  const [error, setError]   = createSignal('');

  // ── Test connection (before saving) ───────────────────────────────────────
  const [testing, setTesting] = createSignal(false);

  // ── Default-database dropdown ──────────────────────────────────────────────
  // The field stays typeable (a DB may not exist yet), but the ▾ button dials the
  // CURRENT form values and offers the live database list via a <datalist>. On a
  // dial failure we clear the list and toast — same body/transport rules as the
  // test path (incl. reusing the stored password on an edit with a blank box).
  const [dbList, setDbList]       = createSignal([]);
  const [loadingDbs, setLoadingDbs] = createSignal(false);
  const loadDatabases = async () => {
    if (!host().trim()) { toast.error('Enter a host first'); return; }
    setLoadingDbs(true);
    try {
      const body = {
        host: host().trim(),
        port: int(port()),
        mysqlUsername: user().trim(),
        password: password(),
        tlsEnabled: !!tlsEnabled(),
      };
      if (isEdit) body.id = init.id;
      if (isAdmin()) {
        body.connectorId = Number(connectorId()) || 0;
        body.sshBastionId = Number(sshBastionId()) || 0;
      } else if (canPickTransport()) {
        body.connectorId = Number(connectorId()) || 0;
      }
      const r = await api.post('/api/connections/list-databases', body);
      if (r.ok && Array.isArray(r.databases) && r.databases.length) {
        setDbList(r.databases);
        toast.success(`Found ${r.databases.length} database${r.databases.length === 1 ? '' : 's'}`);
      } else if (r.ok) {
        setDbList([]);
        toast.info('Connected, but no databases are visible to this user');
      } else {
        setDbList([]);
        toast.error('Please input a valid connection');
      }
    } catch (_) {
      setDbList([]);
      toast.error('Please input a valid connection');
    } finally {
      setLoadingDbs(false);
    }
  };

  // Test the CURRENT form values without saving, reporting via toast. We always
  // send the form values (incl. the chosen transport) so the test reflects what's
  // on screen. On edit we also send the connection id: the server reuses the
  // STORED password when the box is left blank (passwordless / "keep password"),
  // so the test still exercises the form's host + connector/bastion.
  const testConn = async () => {
    const label = name().trim() || 'Connection';
    if (!host().trim()) { toast.error(`${label} — enter a host first`); return; }
    setTesting(true);
    try {
      const body = {
        host: host().trim(),
        port: int(port()),
        mysqlUsername: user().trim(),
        password: password(),
        defaultDatabase: defaultDB().trim(),
        tlsEnabled: !!tlsEnabled(),
      };
      if (isEdit) body.id = init.id;
      // Transport: admins test through either; creators through a connector only.
      if (isAdmin()) {
        body.connectorId = Number(connectorId()) || 0;
        body.sshBastionId = Number(sshBastionId()) || 0;
      } else if (canPickTransport()) {
        body.connectorId = Number(connectorId()) || 0;
      }
      const r = await api.post('/api/connections/test', body);
      if (r.ok) toast.success(`${label} — connection OK`);
      else      toast.error(`${label} — ${r.error || 'connection failed'}`);
    } catch (e) {
      toast.error(`${label} — ${e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      name: name().trim(),
      host: host().trim(),
      port: int(port()),
      mysqlUsername: user().trim(),
      password: password() === '' ? (isEdit ? null : '') : password(),
      defaultDatabase: defaultDB().trim(),
      group: group().trim(),
      color: color(),
      tlsEnabled: !!tlsEnabled(),
      readOnly: !!readOnly(),
      isShared: !!isShared(),
      txnIdleTimeoutSecs: int(timeout()),
      maxTabsPerUser: int(maxTabs()),
      maxPoolConns: int(poolConns()),
      backupRotation: Math.max(0, int(backupRotation())),
    };
    // Tag scoping is admin-only; only admins send shareTags (the backend
    // ignores it for others anyway, but keep the payload honest).
    if (isAdmin()) payload.shareTags = shareTags();
    // Transport (bastion OR connector): admin-only. Non-admins keep the
    // existing values (sticky on edit) without sending them explicitly. The
    // picker guarantees at most one is non-zero.
    if (isAdmin()) {
      payload.sshBastionId = Number(sshBastionId()) || 0;
      payload.connectorId = Number(connectorId()) || 0;
    } else if (canPickTransport()) {
      // Creators may attach a connector; bastions stay admin-only (server preserves it).
      payload.connectorId = Number(connectorId()) || 0;
    }
    try {
      if (isEdit) {
        await api.patch(`/api/connections/${init.id}`, payload);
        toast.success(`Updated ${payload.name}`);
      } else {
        await api.post('/api/connections', payload);
        toast.success(`Created ${payload.name}`);
      }
      props.onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={true} cardClass="conn-form-modal" title={isEdit ? `Edit ${init.name}` : (isDuplicate ? `Duplicate ${init.name}` : 'New Connection')} onClose={props.onClose}>
      <form onSubmit={submit}>
        <div class="form-row">
          <div class="field" style="flex:2;">
            <label class="label" for="cf-name">Name</label>
            <input id="cf-name" class="input" required value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-defdb">Default database</label>
            <div class="input-with-btn">
              <input id="cf-defdb" class="input" list="cf-defdb-list" autocomplete="off"
                     value={defaultDB()} onInput={(e) => setDefaultDB(e.currentTarget.value)} />
              <button type="button" class="btn btn-sm btn-ghost input-btn"
                      title="Connect and list databases"
                      onClick={loadDatabases} disabled={loadingDbs()}>
                {loadingDbs() ? '…' : '▾'}
              </button>
              <Show when={dbList().length}>
                <datalist id="cf-defdb-list">
                  <For each={dbList()}>{(db) => <option value={db} />}</For>
                </datalist>
              </Show>
            </div>
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-group">Group <span class="muted">(optional)</span></label>
            <input id="cf-group" class="input" list="cf-group-list" placeholder="Ungrouped"
                   value={group()} onInput={(e) => setGroup(e.currentTarget.value)} />
            <Show when={props.groups?.length}>
              <datalist id="cf-group-list">
                <For each={props.groups}>{(g) => <option value={g} />}</For>
              </datalist>
            </Show>
          </div>
        </div>

        <div class="form-row">
          <div class="field" style="flex:2;">
            <label class="label" for="cf-host">Host</label>
            <input id="cf-host" class="input" required value={host()} onInput={(e) => setHost(e.currentTarget.value)} />
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-port">Port</label>
            <input id="cf-port" class="input" type="number" min="1" max="65535" required value={port()} onInput={(e) => setPort(e.currentTarget.value)} />
          </div>
        </div>

        <div class="form-row">
          <div class="field" style="flex:1;">
            <label class="label" for="cf-user">MySQL user</label>
            <input id="cf-user" class="input" required value={user()} onInput={(e) => setUser(e.currentTarget.value)} />
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-pw">Password {isEdit && <span class="muted">(leave blank to keep)</span>}{isDuplicate && <span class="muted">(not copied — re-enter)</span>}</label>
            <input id="cf-pw" class="input" type="password" autocomplete="new-password" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} />
          </div>
        </div>

        <div class="form-row">
          <div class="field" style="flex:1;">
            <label class="label" for="cf-timeout">Idle timeout (seconds)</label>
            <input id="cf-timeout" class="input" type="number" min="30" value={timeout()} onInput={(e) => setTimeout_(e.currentTarget.value)} />
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-maxtabs">Max tabs per user</label>
            <input id="cf-maxtabs" class="input" type="number" min="1" value={maxTabs()} onInput={(e) => setMaxTabs(e.currentTarget.value)} />
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-poolconns">Max connections</label>
            <input id="cf-poolconns" class="input" type="number" min="5" max="500" step="1" value={poolConns()} onInput={(e) => setPoolConns(e.currentTarget.value)} />
            <span class="muted" style="font-size:11px;">For grids &amp; browsing (5–500, default 20). Query tabs (up to 50) and AI agents (5) open more on top — keep the total under the server's max_connections.</span>
          </div>
          <div class="field" style="flex:1;">
            <label class="label" for="cf-backuprot">Backup rotation</label>
            <input id="cf-backuprot" class="input" type="number" min="0" value={backupRotation()} onInput={(e) => setBackupRotation(e.currentTarget.value)} />
            <span class="muted" style="font-size:11px;">0 = keep all server backups; N = keep only the newest N (manual + scheduled)</span>
          </div>
        </div>

        <div class="form-row">
          <div class="field" style="flex:1;">
            <label class="label">Color tag <span class="muted" style="font-weight:400;">(safety cue — tints the tab; red = production)</span></label>
            <div class="color-swatches">
              <For each={COLOR_PALETTE}>
                {(c) => (
                  <button
                    type="button"
                    class="color-swatch"
                    classList={{ selected: color() === c.value, none: c.value === '' }}
                    style={c.value ? `background:${c.value};` : ''}
                    title={c.label}
                    aria-label={c.label}
                    onClick={() => setColor(c.value)}
                  >{c.value === '' ? '∅' : (color() === c.value ? '✓' : '')}</button>
                )}
              </For>
            </div>
          </div>
        </div>

        {/* "Connect via" — admin only. Route the MySQL dial through an SSH
            bastion (forward) OR a connector (reverse agent), or go direct. The
            two are mutually exclusive (one select). Manage them in Tools →
            SSH Bastions / Connectors. */}
        <Show when={canPickTransport()}>
          <div class="form-row">
            <label>Connect via</label>
            <select ref={transportSel} class="input" value={transport()}
                    onChange={(e) => setTransport(e.target.value)}>
              <option value="direct">— Direct (no tunnel) —</option>
              <Show when={bastions().length > 0}>
                <optgroup label="SSH bastion (forward)">
                  <For each={bastions()}>
                    {(b) => <option value={`bastion:${b.id}`}>{b.name} ({b.user}@{b.host}:{b.port})</option>}
                  </For>
                </optgroup>
              </Show>
              <Show when={connectors().length > 0}>
                <optgroup label="Connector (reverse agent)">
                  <For each={connectors()}>
                    {(c) => <option value={`connector:${c.id}`}>{c.name} {c.online ? '● online' : '○ offline'}</option>}
                  </For>
                </optgroup>
              </Show>
            </select>
          </div>
        </Show>
        <Show when={!canPickTransport() && (sshBastionId() > 0 || connectorId() > 0)}>
          <div class="form-row">
            <label>Connect via</label>
            <div class="muted mono" style="font-size:12px;">{currentTransportLabel()}</div>
          </div>
        </Show>

        <div class="checkboxes">
          <label class="checkbox">
            <input type="checkbox" checked={tlsEnabled()} onChange={(e) => setTLS(e.currentTarget.checked)} />
            <span>Use TLS</span>
          </label>
          <label class="checkbox">
            <input type="checkbox" checked={readOnly()} onChange={(e) => setReadOnly(e.currentTarget.checked)} />
            <span>Read-only (refuse writes from any user)</span>
          </label>
          <label class="checkbox">
            <input type="checkbox" checked={isShared()} onChange={(e) => setIsShared(e.currentTarget.checked)} />
            <span>Shared with the team</span>
          </label>
        </div>

        {/* Tag-scoped sharing — admins only. No tags selected = whole team. */}
        <Show when={isShared() && isAdmin()}>
          <div class="field share-tags">
            <label class="label">
              Limit to tags <span class="muted" style="font-weight:400;">(none = everyone on the team)</span>
            </label>
            <Show
              when={(allTags() ?? []).length > 0}
              fallback={<div class="muted" style="font-size:12px;">No user tags yet — assign tags to users first (User Management).</div>}
            >
              <div class="share-tag-options">
                <For each={allTags()}>
                  {(t) => (
                    <label class="checkbox share-tag-option">
                      <input type="checkbox" checked={shareTags().includes(t)} onChange={() => toggleTag(t)} />
                      <span>{t}</span>
                    </label>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>

        <Show when={error()}><div class="error-text">{error()}</div></Show>

        <div class="modal-actions">
          <button type="button" class="btn btn-ghost" onClick={testConn} disabled={testing() || saving()}>
            {testing() ? 'Testing…' : 'Test connection'}
          </button>
          <div class="spacer" />
          <button type="button" class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
          <button type="submit" class="btn" disabled={saving()}>{saving() ? 'Saving…' : (isEdit ? 'Save changes' : 'Create connection')}</button>
        </div>
      </form>
    </Modal>
  );
}
